/**
 * "Minha loja" — onde a filial escolhida fica guardada, e por que ali.
 *
 * ## A escolha e do APARELHO, e o documento diz isso duas vezes
 *
 * `PWA §10.2` recusa geolocalizacao no MVP e nomeia o substituto:
 *
 * > *"1. **Selecao manual de filial**, persistida em `kv.storeId`, com 'trocar de loja' sempre
 * > visivel no cabecalho."*
 *
 * `API §6.5` confirma o lado do servidor: `POST /v1/me/preferred-store` e rota **prevista**, e
 * *"no MVP a preferencia vive no aparelho (`kv.storeId`)"*. Nao ha o que sincronizar.
 *
 * ## Classe de sensibilidade (§30, ADR-0012): confirmado antes de gravar
 *
 * Filial e **dado publico**: nome, endereco, CNPJ e licenca sanitaria de cada uma sao de
 * publicacao obrigatoria (REG-001) e vem de `GET /v1/stores`, que e `public, s-maxage=600`.
 * Nao e dado do titular, nao e dado de saude e nao diz nada sobre quem usa o aparelho — dois
 * moradores da mesma rua escolhem a mesma filial. Por isso ela pode ser gravada em claro, e por
 * isso `PWA §7.2` marca o store `kv` como **`limpaNoLogout: false`**: *"tema, `saveData` e
 * ultimo `storeId` sao preferencia do aparelho, nao do titular"*.
 *
 * ## Dois lugares, e nao um — e isso vem dos dois documentos, nao de zelo
 *
 * | Onde | Papel | Fonte |
 * | ---- | ----- | ----- |
 * | `localStorage['storeId']` | **espelho sincrono de boot**: lido antes da primeira renderizacao, para o cabecalho nao piscar "Escolher loja" para quem ja escolheu | `PWA §7.1` — localStorage guarda "um punhado de flags sincronas de boot (tema, `saveData` manual, ultimo `storeId`)" |
 * | IndexedDB `kv['storeId']` | **copia duravel**: `kv` nao e limpo no logout | `PWA §7.2` |
 *
 * A dupla nao e redundancia inutil: a purga do `PWA §7.5`, passo 7, preserva **apenas** `tema`
 * e `saveData` no `localStorage`, entao o espelho **e apagado no logout** — enquanto o `kv`
 * sobrevive, porque o documento diz que sobrevive. Sem a copia duravel, sair da conta faria a
 * pessoa reescolher a filial, que e o oposto de "preferencia do aparelho". Sem o espelho
 * sincrono, todo boot renderiza um quadro sem filial antes do `await`.
 *
 * **DUVIDA (reportada):** o `PWA §7.1` escreve "ultimo `storeId`", que sugere guardar so o
 * identificador. Guardamos tambem o **nome de vitrine** da filial, no mesmo registro, porque o
 * cabecalho do `DESIGN_SYSTEM §7.2` mostra `Filial Centro · Aberta ate 22h` **no primeiro
 * quadro**, e buscar `GET /v1/stores` antes de desenhar o cabecalho trocaria o "pisca" por uma
 * requisicao no caminho critico. O nome e o mesmo dado publico do `id`. O leitor aceita as duas
 * formas (string crua e objeto), entao um valor gravado por uma versao anterior nao quebra.
 */

/** A chave e a que `PWA §7.1`/`§7.2` nomeiam, e a mesma de `CHAVES_DE_LOCALSTORAGE`. */
export const CHAVE_DA_LOJA = 'storeId';

/** O store do IndexedDB do `PWA §7.2`. */
export const STORE_KV = 'kv';

export interface LojaPreferida {
  readonly id: string;
  /** Nome de vitrine (`store.name`), para o cabecalho desenhar sem requisicao. */
  readonly nome: string;
}

type ArmazenamentoSincrono = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/**
 * As portas assincronas (IndexedDB). Injetaveis pelo mesmo motivo de `purga.ts`: a protecao
 * precisa ser verificavel em milissegundos, sem navegador.
 */
export interface PortasDaLoja {
  readonly lerDoBanco: () => Promise<LojaPreferida | null>;
  readonly gravarNoBanco: (loja: LojaPreferida) => Promise<void>;
}

// ---------------------------------------------------------------------------
// Leitura e escrita sincronas — o espelho de boot
// ---------------------------------------------------------------------------

/**
 * Le o espelho sincrono. `null` quando nao ha escolha, quando o valor esta corrompido, ou
 * quando nao ha `localStorage` (modo privado do Safari antigo, contexto sem DOM).
 *
 * Nunca lanca: uma preferencia ilegivel e "ainda nao escolheu", que a interface ja sabe tratar.
 * Lancar aqui derrubaria o boot inteiro por causa de uma string malformada.
 */
export function lerLojaPreferida(
  armazenamento: ArmazenamentoSincrono | null = tentarLocalStorage(),
): LojaPreferida | null {
  if (armazenamento === null) return null;
  let bruto: string | null = null;
  try {
    bruto = armazenamento.getItem(CHAVE_DA_LOJA);
  } catch {
    return null;
  }
  return interpretar(bruto);
}

/**
 * Aceita as duas formas: o objeto `{ id, nome }` que esta versao grava e a string crua que o
 * `PWA §7.1` descreve. A string crua vira uma loja **sem nome**, e o cabecalho a completa
 * quando `GET /v1/stores` chegar.
 */
export function interpretar(bruto: string | null): LojaPreferida | null {
  if (bruto === null || bruto === '') return null;

  if (!bruto.startsWith('{')) return { id: bruto, nome: '' };

  try {
    const objeto: unknown = JSON.parse(bruto);
    if (typeof objeto !== 'object' || objeto === null) return null;
    const { id, nome } = objeto as { id?: unknown; nome?: unknown };
    if (typeof id !== 'string' || id === '') return null;
    return { id, nome: typeof nome === 'string' ? nome : '' };
  } catch {
    return null;
  }
}

export function serializar(loja: LojaPreferida): string {
  return JSON.stringify({ id: loja.id, nome: loja.nome });
}

/**
 * Grava o espelho sincrono. Engole a falha de cota e de modo privado: nao poder LEMBRAR a
 * escolha nao pode impedir a pessoa de FAZER a escolha — o custo e reescolher na proxima visita.
 */
export function gravarLojaPreferida(
  loja: LojaPreferida,
  armazenamento: ArmazenamentoSincrono | null = tentarLocalStorage(),
): void {
  if (armazenamento === null) return;
  try {
    armazenamento.setItem(CHAVE_DA_LOJA, serializar(loja));
  } catch {
    // Cota estourada ou `localStorage` bloqueado. Ver acima.
  }
}

function tentarLocalStorage(): ArmazenamentoSincrono | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// A copia duravel — IndexedDB `kv`
// ---------------------------------------------------------------------------

/**
 * Le a copia duravel e **reescreve o espelho** com ela.
 *
 * E este o caminho que devolve a filial depois de um logout, que apagou o espelho e nao tocou
 * no `kv`. Devolve `null` quando nao ha nada guardado ou quando o IndexedDB nao esta
 * disponivel — a interface trata como "ainda nao escolheu".
 */
export async function reidratarLojaPreferida(
  portas: PortasDaLoja,
  armazenamento: ArmazenamentoSincrono | null = tentarLocalStorage(),
): Promise<LojaPreferida | null> {
  let doBanco: LojaPreferida | null = null;
  try {
    doBanco = await portas.lerDoBanco();
  } catch {
    return null;
  }
  if (doBanco === null) return null;
  gravarLojaPreferida(doBanco, armazenamento);
  return doBanco;
}

/** Grava nos dois lugares. O espelho primeiro: ele e sincrono e nao pode ficar atras. */
export async function lembrarLojaPreferida(
  loja: LojaPreferida,
  portas: PortasDaLoja,
  armazenamento: ArmazenamentoSincrono | null = tentarLocalStorage(),
): Promise<void> {
  gravarLojaPreferida(loja, armazenamento);
  try {
    await portas.gravarNoBanco(loja);
  } catch {
    // Sem IndexedDB (contexto inseguro, cota, modo privado), a escolha ainda vale nesta
    // instalacao — ela so nao sobrevive ao logout.
  }
}
